import { Menu } from '@mui/icons-material';
import {
  OrganizationType,
  type PositionResponse,
  RecruitmentStatus,
  type SearchV1PeopleListParams,
  type SlimCoachResponse,
  type SlimOrganizationRes,
  type StateResponse,
} from '@on3/api';
import { On3IconSearch } from '@on3/icons';
import { type OptionType } from '@on3/ui-lib/components/AutoComplete/AutoComplete';
import { OrganizationAutoComplete } from '@on3/ui-lib/components/AutoComplete/OrganizationAutoComplete';
import { Checkbox } from '@on3/ui-lib/components/Checkbox/Checkbox';
import { Input } from '@on3/ui-lib/components/Input/Input';
import { Select } from '@on3/ui-lib/components/Select/Select';
import { CloseButton } from '@on3/ui-lib/components/Svg/CloseButton';
import { useSite } from '@on3/ui-lib/index';
import { formatFilterOptions } from '@on3/ui-lib/utils/formatters';
import { useDynamicRoute } from '@on3/ui-lib/utils/hooks/useDynamicRoute';
import { MobileMPU } from '@public/components/Ads/MobileMPU';
import { MPU } from '@public/components/Ads/MPU';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useReducer, useState } from 'react';

import styles from './RankingsSearchLayout.module.scss';

interface RankingsSearchLayoutProps {
  children: React.ReactNode;
  positions: PositionResponse[];
  className?: string;
  yearsData: number[];
  statesData: StateResponse[];
}

// TODO: Audit whether we should move this to a common location
enum SearchKeys {
  ARTICLE_TAGGING_ENABLED = 'articleTaggingEnabled',
  ATHLETE_VERIFIED = 'athleteVerified',
  ATTENDED_OR_GRADUATED_FROM_ORG_KEY = 'attendedOrGraduatedFromOrgKey',
  COLLEGE_ORG_KEYS = 'collegeOrgKeys',
  COMMITTED_ORGANIZATION_KEY = 'committedOrganizationKey',
  CURRENT_ORGANIZATION_KEY = 'currentOrganizationKey',
  CURRENT_ORGANIZATION_LEVEL = 'currentOrganizationLevel',
  DISTANCE_FROM_LATITUDE = 'distanceFromLatitude',
  DISTANCE_FROM_LONGITUDE = 'distanceFromLongitude',
  DISTANCE_FROM_ORG_KEY = 'distanceFromOrgKey',
  DISTANCE_FROM_ZIPCODE = 'distanceFromZipCode',
  DISTANCE_FROM_MILES = 'distanceFromMiles',
  FIRST_NAME = 'firstName',
  GOES_BY = 'goesBy',
  HIGH_SCHOOL_ORG_KEYS = 'highSchoolOrgKeys',
  HOME_STATE_KEY = 'homeStateKey',
  IS_DRAFT_PICK = 'isDraftPick',
  IS_FIVE_STAR_PLUS = 'isFiveStarPlus',
  LAST_NAME = 'lastName',
  LIMIT = 'limit',
  MAX_CLASS_YEAR = 'maxClassYear',
  MAX_CONSENSUS_NATIONAL_RANK = 'maxConsensusNationalRank',
  MAX_NIL_VALUATION = 'maxNilValuation',
  MAX_OVERALL_NATIONAL_RANK = 'maxOverallNationalRank',
  MAX_STARS = 'maxStars',
  MIN_CLASS_YEAR = 'minClassYear',
  MIN_HEIGHT = 'minHeight',
  MIN_NIL_VALUATION = 'minNilValuation',
  MIN_STARS = 'minStars',
  MIN_WEIGHT = 'minWeight',
  OFFICIAL_VISIT_END_DATE = 'officialVisitEndDate',
  OFFICIAL_VISIT_ORG_KEYS = 'officialVisitOrgKeys',
  OFFICIAL_VISIT_START_DATE = 'officialVisitStartDate',
  PAGE = 'page',
  POSITION_KEY = 'positionKey',
  RECRUITED_BY_PERSON_KEY = 'recruitedByPersonKey',
  RECRUITING_STATUSES = 'recruitingStatuses',
  RECRUITMENT_INTEREST_ORG_KEYS = 'recruitmentInterestOrgKeys',
  SEARCH_TEXT = 'searchText',
  SPORT_KEY = 'sportKey',
  YEAR = 'year',
}

const playerStarOptions = [
  { label: 'Any', value: '' },
  { label: '3-Stars', value: '3' },
  { label: '4-Stars', value: '4' },
  { label: '5-Stars', value: '5' },
];

const recruitmentStatusOptions = Object.values(RecruitmentStatus)
  .filter(
    (status) =>
      status !== RecruitmentStatus.None &&
      status !== RecruitmentStatus.Decommitted,
  )
  .map((status) => ({
    label: status,
    value: status,
  }));

type ActionType =
  | { type: 'search' }
  | { type: 'searched' }
  | {
      type: 'update';
      field: string;
      value: any;
    }
  | { type: 'reset' }
  | { type: 'count'; value: number };

interface SearchState {
  searching: boolean;
  values: SearchV1PeopleListParams & {
    distanceFromOrgKey?: number;
    distanceFromZipCode?: number;
  };
  filterCount: number;
}
const initialState: SearchState = {
  searching: false,
  values: {},
  filterCount: 0,
};
const reducer = (state: SearchState, action: ActionType) => {
  switch (action.type) {
    case 'search':
      return { ...state, searching: true };
    case 'searched':
      return { ...state, searching: false };
    case 'update':
      return {
        ...state,
        values: { ...state.values, [action.field]: action.value },
      };
    case 'reset':
      return {
        ...state,
        values: { ...initialState.values },
      };

    case 'count':
      return { ...state, filterCount: action.value };
    default:
      return state;
  }
};

const autoCompleteData = (
  value?:
    | SlimOrganizationRes
    | SlimCoachResponse
    | (SlimOrganizationRes | SlimCoachResponse)[],
): OptionType[] | undefined => {
  if (Array.isArray(value)) {
    const options = value
      .filter((item) => item?.key && item.fullName)
      .map((item) => ({
        key: item.key,
        label: item.fullName ?? 'Unknown',
        value: item.key,
      }));

    return options.length > 0 ? options : undefined;
  } else if (value?.key && value.fullName) {
    return [
      {
        key: value.key,
        label: value.fullName ?? 'Unknown',
        value: value.key,
      },
    ];
  }

  return undefined;
};

export const RankingsSearchLayout = ({
  children,
  yearsData,
  statesData,
  className,
  positions,
}: RankingsSearchLayoutProps) => {
  const { sportsData } = useSite();
  const { formatHref } = useDynamicRoute();
  const router = useRouter();

  const initial: SearchState = {
    ...initialState,
    values: {
      ...router.query,
    },
  };

  const [state, dispatch] = useReducer(reducer, initial);

  const [allCollapsed, setAllCollapsed] = useState<boolean>(true);
  const [attendedOrGraduatedFrom, setAttendedOrGraduatedFrom] =
    useState<boolean>(!!state.values.attendedOrGraduatedFromOrgKey);
  const [interestedIn, setInterestedIn] = useState<boolean>(
    !!state.values.recruitmentInterestOrgKeys?.length,
  );

  const handleResets = () => {
    setAttendedOrGraduatedFrom(false);
    setInterestedIn(false);

    dispatch({ type: 'reset' });
    router.replace(
      formatHref({
        options: {
          resetQuery: true,
        },
      }),
    );
  };

  const handleFieldUpdate = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
      | React.MouseEvent<HTMLButtonElement>,
  ) => {
    e.preventDefault();
    const target = e.currentTarget as
      | HTMLInputElement
      | HTMLSelectElement
      | HTMLButtonElement;

    const { name, value } = target;

    dispatch({
      type: 'update',
      field: name,
      value,
    });
  };

  const sportsList = formatFilterOptions({
    data: sportsData,
    valueKey: 'key',
    labelKey: 'name',
    addAll: true,
  });

  const positionList = formatFilterOptions({
    data: positions,
    labelKey: 'name',
    valueKey: 'key',
    addAll: true,
  });

  const handleAutoCompleteChange = (value: OptionType[], field?: string) => {
    if (!field) return;

    dispatch({
      type: 'update',
      field,
      value: value.map((v) => v.key),
    });
  };

  const handleRecruitStatusChange = (
    statusValue: string,
    isChecked: boolean,
  ) => {
    const status = Array.isArray(state.values.recruitingStatuses)
      ? state.values.recruitingStatuses
      : state.values.recruitingStatuses
        ? [state.values.recruitingStatuses]
        : [];

    const newStatus = isChecked
      ? [...status, statusValue]
      : status.filter((s) => s !== statusValue);

    const validStatuses = Object.values(RecruitmentStatus);
    const filteredStatus = newStatus.filter((s) =>
      validStatuses.includes(s as RecruitmentStatus),
    );

    dispatch({
      type: 'update',
      field: SearchKeys.RECRUITING_STATUSES,
      value: filteredStatus,
    });

    if (newStatus.length === 0) {
      dispatch({
        type: 'update',
        field: SearchKeys.COMMITTED_ORGANIZATION_KEY,
        value: undefined,
      });
    }
  };
  const handleAttendedOrGraduatedFrom = useCallback((value: boolean) => {
    setAttendedOrGraduatedFrom(value);
    if (value === false) {
      dispatch({
        type: 'update',
        field: SearchKeys.ATTENDED_OR_GRADUATED_FROM_ORG_KEY,
        value: undefined,
      });
    }
  }, []);
  const handleInterestedIn = useCallback((value: boolean) => {
    setInterestedIn(value);
    if (value === false) {
      dispatch({
        type: 'update',
        field: SearchKeys.RECRUITMENT_INTEREST_ORG_KEYS,
        value: undefined,
      });
    }
  }, []);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      dispatch({ type: 'search' });

      const { page, ...query } = router.query;

      const validSearchKeys = new Set(Object.values(SearchKeys));

      // Only send params that pertain to Advanced Search
      const params = Object.entries(state.values).reduce(
        (acc, [key, value]) => {
          if (key === 'year') {
            delete acc[key];
          } else if (validSearchKeys.has(key as SearchKeys)) {
            value ? (acc[key] = value) : delete acc[key];
          } else {
            delete acc[key];
          }

          return acc;
        },
        { ...query },
      );

      router
        .push({
          pathname: '/rivals/search',
          query: params,
        })
        .then(() => {
          dispatch({ type: 'searched' });
        });
    },
    [router, state.values],
  );

  useEffect(() => {
    const {
      values: { searchText, limit, page, ...filters },
    } = state;

    const filteredFilters = Object.entries(filters).filter(
      ([key, value]) =>
        value !== '' &&
        value !== false &&
        value !== undefined &&
        !(Array.isArray(value) && value.length === 0),
    );

    const filterCount = filteredFilters.length;

    if (state.filterCount !== filterCount) {
      dispatch({ type: 'count', value: filterCount });
    }
  }, [state]);

  return (
    <main className={styles.block}>
      <div className={styles.search}>
        <header>
          <h2>Search</h2>
        </header>

        <form onSubmit={handleSubmit}>
          <Input
            className={styles.searchInput}
            id="search"
            label="Search by Name"
            name={SearchKeys.SEARCH_TEXT}
            onChange={handleFieldUpdate}
            placeholder="Search Players"
            prefix={
              <button
                className={styles.btnSearchIcon}
                disabled={state.searching}
                name="icon-search"
                type="submit"
              >
                <On3IconSearch />
              </button>
            }
            srLabel
            suffix={
              state.values.searchText && (
                <button
                  className={styles.clear}
                  disabled={state.searching}
                  name={SearchKeys.SEARCH_TEXT}
                  onClick={handleFieldUpdate}
                  type="button"
                >
                  <span className="sr-only">Clear</span>
                  <CloseButton />
                </button>
              )
            }
            value={state.values.searchText}
          />
          <button
            className={styles.btnSearchDesktop}
            disabled={state.searching}
            name="search"
            type="submit"
          >
            Search
          </button>
        </form>
      </div>
      {children}
      <aside className={styles.filtersContainer}>
        <MPU className={styles.ad} id="mpu_top" />
        <div className={styles.filters}>
          <header className={styles.filtersHeader}>
            <span className={styles.heading}>
              Filter Players
              {state.filterCount > 0 && ` (${state.filterCount})`}
            </span>
            <button
              name="collapse-all"
              onClick={() => {
                const newCollapseState = !allCollapsed;

                setAllCollapsed(newCollapseState);
              }}
              type="button"
            >
              <span className="sr-only">Collapse</span>
              <Menu />
            </button>
          </header>
          <div
            className={styles.allOptions}
            data-status={allCollapsed ? 'closed' : 'open'}
          >
            <div className={styles.container}>
              <Select
                label="Sport"
                name={SearchKeys.SPORT_KEY}
                onChange={handleFieldUpdate}
                options={sportsList}
                value={state.values.sportKey?.toString()}
              />

              <Select
                disabled={!positionList.length}
                label="Position"
                name={SearchKeys.POSITION_KEY}
                onChange={handleFieldUpdate}
                options={positionList}
                value={state.values.positionKey?.toString()}
              />
              <div className={styles.vitals}>
                <Input
                  label="Min height"
                  name={SearchKeys.MIN_HEIGHT}
                  onChange={handleFieldUpdate}
                  type="number"
                  value={state.values.minHeight}
                />
                <Input
                  label="Min weight"
                  name={SearchKeys.MIN_WEIGHT}
                  onChange={handleFieldUpdate}
                  type="number"
                  value={state.values.minWeight}
                />
              </div>
              <Select
                id={SearchKeys.HOME_STATE_KEY}
                label="Home State"
                name={SearchKeys.HOME_STATE_KEY}
                onChange={handleFieldUpdate}
                options={[
                  { label: 'All', value: '' },
                  ...statesData.map((st) => ({
                    label: String(st.name),
                    value: String(st.key),
                  })),
                ]}
                value={String(state.values.homeStateKey)}
              />
              <OrganizationAutoComplete
                id={SearchKeys.HIGH_SCHOOL_ORG_KEYS}
                label="High School"
                name={SearchKeys.HIGH_SCHOOL_ORG_KEYS}
                onChange={handleAutoCompleteChange}
                type={OrganizationType.HighSchool}
                value={[]}
              />
            </div>

            <div className={styles.container} data-name="recruiting">
              <h3>Recruiting</h3>
              <div className={styles.columns}>
                <Select
                  label="Class"
                  name={SearchKeys.MIN_CLASS_YEAR}
                  onChange={handleFieldUpdate}
                  options={[
                    { label: 'Any', value: '' },
                    ...yearsData.map((y) => ({
                      label: String(y),
                      value: String(y),
                    })),
                  ]}
                  value={state.values.minClassYear?.toString()}
                />
                <span className={styles.label}>To</span>
                <Select
                  label="Class"
                  name={SearchKeys.MAX_CLASS_YEAR}
                  onChange={handleFieldUpdate}
                  options={[
                    { label: 'Any', value: '' },
                    ...yearsData.map((y) => ({
                      label: String(y),
                      value: String(y),
                    })),
                  ]}
                  srLabel
                  value={state.values.maxClassYear?.toString()}
                />
              </div>
              <div className={styles.columns}>
                <Select
                  label="Player Stars"
                  name={SearchKeys.MIN_STARS}
                  onChange={handleFieldUpdate}
                  options={playerStarOptions}
                  value={state.values.minStars?.toString()}
                />
                <span className={styles.label}>To</span>
                <Select
                  label="Player Stars"
                  name={SearchKeys.MAX_STARS}
                  onChange={handleFieldUpdate}
                  options={playerStarOptions}
                  srLabel
                  value={state.values.maxStars?.toString()}
                />
              </div>
            </div>
            <div className={styles.container}>
              <h3>Status</h3>
              <div className={styles.statusCheckboxes}>
                <span className={styles.grayLabel}>
                  Only include players with status:
                </span>
                <div>
                  {recruitmentStatusOptions.map((status, index) => (
                    <Checkbox
                      disabled={interestedIn || attendedOrGraduatedFrom}
                      id={status.label}
                      key={`${state.values.recruitingStatuses?.length}-${index}`}
                      label={status.label}
                      onChange={(isChecked) =>
                        handleRecruitStatusChange(status.value, isChecked)
                      }
                      value={Boolean(
                        state.values.recruitingStatuses?.includes(status.value),
                      )}
                    />
                  ))}
                  <div data-show={!!state.values.recruitingStatuses?.length}>
                    <OrganizationAutoComplete
                      id={SearchKeys.COMMITTED_ORGANIZATION_KEY}
                      label="College Team"
                      limit={1}
                      name={SearchKeys.COMMITTED_ORGANIZATION_KEY}
                      onChange={handleAutoCompleteChange}
                      type={OrganizationType.College}
                      value={[]}
                    />
                  </div>
                </div>
                <span className={styles.grayLabel}>Or status of:</span>
                <div>
                  <Checkbox
                    disabled={
                      !!state.values.recruitingStatuses?.length || interestedIn
                    }
                    id="attended-or-graduated-from"
                    key={`attended-${attendedOrGraduatedFrom}`}
                    label="Attended / Graduated"
                    onChange={handleAttendedOrGraduatedFrom}
                    value={attendedOrGraduatedFrom}
                  />
                  <div data-show={attendedOrGraduatedFrom}>
                    <OrganizationAutoComplete
                      id={SearchKeys.ATTENDED_OR_GRADUATED_FROM_ORG_KEY}
                      label="College Team"
                      limit={1}
                      name={SearchKeys.ATTENDED_OR_GRADUATED_FROM_ORG_KEY}
                      onChange={handleAutoCompleteChange}
                      type={OrganizationType.College}
                      value={[]}
                    />
                  </div>

                  <Checkbox
                    disabled={
                      attendedOrGraduatedFrom ||
                      !!state.values.recruitingStatuses?.length
                    }
                    id="interested-in"
                    key={`interested-${interestedIn}`}
                    label="Interested In"
                    onChange={handleInterestedIn}
                    value={interestedIn}
                  />
                  <div data-show={interestedIn}>
                    <OrganizationAutoComplete
                      id={SearchKeys.RECRUITMENT_INTEREST_ORG_KEYS}
                      label="College Team"
                      limit={1}
                      name={SearchKeys.RECRUITMENT_INTEREST_ORG_KEYS}
                      onChange={handleAutoCompleteChange}
                      type={OrganizationType.College}
                      value={[]}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.buttons}>
              <button
                className={styles.btnSearch}
                disabled={state.searching}
                name="search"
                onClick={handleSubmit}
                type="button"
              >
                Search
              </button>
              <button
                className={styles.btnReset}
                name="reset"
                onClick={handleResets}
                type="button"
              >
                Reset all
              </button>
            </div>
          </div>
        </div>
        <MPU className={styles.ad} id="mpu_middle" />
      </aside>
      <div className={styles.mobileAd}>
        <MobileMPU id="mobile_mpu_middle" incontent />
      </div>
    </main>
  );
};
